import type { HaftkhabisEvent, HaftkhabisView } from "@bing/engine-haftkhabis";
import { fa } from "../i18n/fa";
import { dealMoment, momentsOf, movesIn, type Moment } from "./haftkhabis-moments";

/*
 * EVERY EFFECT AT THE هفت خبیث TABLE IS SHOWN EXACTLY ONCE (the engine's README, «Events»): the frames carry the newest
 * 24 events, each with a `seq` that grows over the match; a client shows those above the last it showed, and on a first
 * frame (a mount, a reconnect) it takes `eventSeq` as the last shown and shows nothing, so no replay and no burst.
 * `advance` is that rule, pure: same frames in, same moments out, whatever React re-renders and however often. A frame's
 * moments (`haftkhabis-moments.ts`: what it shows and in what order) wait behind the one on screen; the table's
 * `useHaftkhabisFeed` only holds this state and runs the timers.
 */

/** A frame's worth of events is at most six (a play, its press, its effect, a reshuffle, the draw…). Far
 *  more means frames were missed (a reconnect that kept the table mounted): the same as a first frame. */
export const MAX_FRAME_EVENTS = 8;
/** How long a banner stays with nothing to replace it. */
export const EFFECT_MS = 1600;
/** The most moves ever waiting behind the one on screen: past it (a burst of frames, a thawed socket) the oldest
 *  go, so the table never lags behind its frames. A round's end is never one of them. */
export const MAX_WAITING = 3;

export interface Feed {
  /** The last frame seen. */
  readonly frame: HaftkhabisView;
  /** The seq of the last event taken. */
  readonly last: number;
  /** The card the live top lies on, where the frames saw it. */
  readonly under: string | null;
  /** The moments: the first is on screen, the rest wait. */
  readonly queue: readonly Moment[];
  /** How far the moment on screen has got: an index into its scenes. */
  readonly step: number;
  /** Tells apart the moments of a rematch on the same mount. */
  readonly serial: number;
}

/** Is this frame a fresh match's opening? (A resume into a later round's first move draws no deal: it cannot be
 *  told from a reconnect. The match's first frame after a re-join says `resumed`.) */
const isOpening = (view: HaftkhabisView): boolean =>
  view.round === 0 && view.moveCount === 0 && view.phase !== "gameOver";

/** A first frame: everything before it is the past, but for a fresh match's deal. */
export function initialFeed(view: HaftkhabisView, visible = true, serial = 0): Feed {
  return {
    frame: view,
    last: view.eventSeq,
    under: null,
    queue: visible && isOpening(view) ? [dealMoment(`${serial}:open`, view)] : [],
    step: 0,
    serial,
  };
}

/** The queue with its backlog bounded: the moment on screen and every opening always stay. */
function bounded(queue: readonly Moment[]): readonly Moment[] {
  const waiting = queue.slice(1).filter((m) => !m.opening);
  if (waiting.length <= MAX_WAITING) return queue;
  const dropped = new Set(waiting.slice(0, waiting.length - MAX_WAITING).map((m) => m.id));
  return queue.filter((m) => !dropped.has(m.id));
}

/**
 * The next state of the feed for a new frame. The heart of «exactly once»: only events above `last` are taken, and
 * `last` only grows while the match lasts. `visible` false (the app is hidden): events pass silently: nothing queues,
 * no timer is asked for. Frames that were missed (a reconnect's tail, a thawed socket) pass the same way: to narrate
 * them now would be a burst.
 */
export function advance(feed: Feed, view: HaftkhabisView, visible = true): Feed {
  // A rematch on the same mount: the seq starts over, and the old match's cursor would hide it.
  if (view.eventSeq < feed.last) return initialFeed(view, visible, feed.serial + 1);
  const fresh = view.events.filter((e) => e.seq > feed.last);
  const before = feed.frame;
  const base: Feed = {
    ...feed,
    frame: view,
    last: view.eventSeq,
    under: view.round !== before.round ? null : view.top !== before.top ? before.top : feed.under,
  };
  if (fresh.length === 0) return base;
  // The round on the table has had a move: no card or deal of its opening may stand any more. They would
  // hold a live hand for their whole span, under a window that is running.
  const movedOn = view.phase !== "gameOver" && view.moveCount > 0;
  const ended = fresh.some((e) => e.kind === "roundEnd");
  if (!visible || (ended && movedOn) || movesIn(fresh) > 1 || fresh.length > MAX_FRAME_EVENTS)
    return { ...base, queue: [], step: 0, under: null };
  const kept = movedOn ? feed.queue.filter((m) => !m.opening) : feed.queue;
  const moments = momentsOf(feed.last + 1, before, view, fresh, feed.under);
  const headKept = kept[0]?.id === feed.queue[0]?.id;
  return {
    ...base,
    queue: bounded([...kept, ...moments]),
    step: headKept ? feed.step : 0,
    // a round's first frame: its top lies on nothing
    under: ended ? null : base.under,
  };
}

/** The moment on screen reached its scene `step`. */
export function stepTo(feed: Feed, id: string, step: number): Feed {
  return feed.queue[0]?.id === id && step > feed.step ? { ...feed, step } : feed;
}

/** The moment on screen is over: the next one, or the live frame. */
export function momentDone(feed: Feed, id: string): Feed {
  return feed.queue[0]?.id === id ? { ...feed, queue: feed.queue.slice(1), step: 0 } : feed;
}

/**
 * Your turn's window opened (a `turn` frame): the server's opening beat is over, whatever this screen still draws of it
 * (a reconnect that landed inside the opening starts its deal late). A live hand is never held under a running clock.
 */
export function windowOpened(feed: Feed): Feed {
  if (!feed.queue.some((m) => m.opening)) return feed;
  const queue = feed.queue.filter((m) => !m.opening);
  return { ...feed, queue, step: queue[0]?.id === feed.queue[0]?.id ? feed.step : 0 };
}

/** The app went away: nothing it would come back to is worth waiting for. */
export function goneHidden(feed: Feed): Feed {
  if (feed.queue.length === 0) return feed;
  return { ...feed, queue: [], step: 0, under: null };
}

/** One mark a banner's effect lands on a seat. */
export interface SeatMark {
  readonly seat: number;
  readonly text: string;
  readonly tone: "bad" | "good";
}

export interface TableEffect {
  readonly line: string;
  readonly mark: SeatMark | null;
}

const suitWord = (suit: keyof typeof fa.haftkhabis.suits): string => fa.haftkhabis.suits[suit];

/** The words of one effect and where it lands, or null for an event with no banner. */
export function effectOf(
  event: HaftkhabisEvent,
  you: number,
  name: (seat: number) => string,
  turn: number,
): TableEffect | null {
  const t = fa.haftkhabis;
  switch (event.kind) {
    case "seven":
      return {
        line: t.fxSeven(event.penalty),
        mark: { seat: turn, text: t.markPlus(event.penalty), tone: "bad" },
      };
    case "penaltyPaid":
      return {
        line:
          event.count === 0
            ? t.fxNothingToDraw
            : event.seat === you
              ? t.fxPaidYou(event.count)
              : t.fxPaid(name(event.seat), event.count),
        mark:
          event.count === 0
            ? null
            : { seat: event.seat, text: t.markPlus(event.count), tone: "bad" },
      };
    case "bonus":
      return {
        line: event.seat === you ? t.fxBonusYou : t.fxBonus(name(event.seat)),
        mark: { seat: event.seat, text: t.markBonus, tone: "good" },
      };
    case "skip":
      return {
        line: event.skipped === you ? t.fxSkipYou : t.fxSkip(name(event.skipped)),
        mark: { seat: event.skipped, text: t.markSkip, tone: "bad" },
      };
    case "reverse":
      return { line: t.fxReverse, mark: null };
    case "suit":
      return {
        line:
          event.seat === you
            ? t.fxSuitYou(suitWord(event.suit))
            : t.fxSuit(name(event.seat), suitWord(event.suit)),
        mark: null,
      };
    case "target":
      if (event.via === "draw")
        return {
          line: event.target === you ? t.fxTargetDrawYou : t.fxTargetDraw(name(event.target)),
          mark: { seat: event.target, text: t.markPlus(1), tone: "bad" },
        };
      return {
        line:
          event.target === you
            ? t.fxTargetYou(name(event.seat))
            : event.seat === you
              ? t.fxTargetByYou(name(event.target))
              : t.fxTarget(name(event.seat), name(event.target)),
        mark: null,
      };
    case "take":
      return {
        line:
          event.target === you
            ? t.fxTakeYou(name(event.giver))
            : event.giver === you
              ? t.fxTakenFromYou(name(event.target))
              : t.fxTake(name(event.target), name(event.giver)),
        mark: { seat: event.target, text: t.markPlus(1), tone: "bad" },
      };
    case "draw":
      if (event.count === 0) return { line: t.fxNothingToDraw, mark: null };
      if (event.reason !== "bonus") return null;
      return {
        line: event.seat === you ? t.fxBonusDrawYou : t.fxBonusDraw(name(event.seat)),
        mark: { seat: event.seat, text: t.markPlus(event.count), tone: "bad" },
      };
    case "press":
      return {
        line: event.seat === you ? t.fxPressYou : t.fxPress(name(event.seat)),
        mark: { seat: event.seat, text: t.takPress, tone: "good" },
      };
    case "catch":
      return {
        line:
          event.count === 0
            ? t.fxNothingToDraw
            : event.target === you
              ? t.fxCaughtYou(name(event.by))
              : event.by === you
                ? t.fxCaughtByYou(name(event.target))
                : t.fxCaught(name(event.by), name(event.target)),
        mark:
          event.count === 0
            ? null
            : { seat: event.target, text: t.markPlus(event.count), tone: "bad" },
      };
    case "reshuffle":
      return { line: t.fxReshuffle, mark: null };
    case "roundStart": // D1: only ever said over a special turned up, on its way back
      return { line: t.fxTurnedBack, mark: null };
    default:
      return null;
  }
}

export type { HaftkhabisEvent };
